import {Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {AirlineModel} from '../../../models/AirlineModel';
import {Airport, Destination, DealFrequency, DEAL_FREQUENCY_CLASS_MAP} from '../../../models/AirportModel';
import {checkDepartureAirportLimit, LimitReachedAction} from '../../../helpers/user-departure-airport-limits.utils';

@Component({
    selector: 'search-select',
    templateUrl: './search-select-form.component.html',
    styleUrls: ['./search-select-form.component.css'],
})
export class SearchSelectComponent implements OnInit {
    @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

    @Input() placeholder = 'Search...';
    @Input() currentCount = 0;
    @Input() maxCount = 10;
    @Input() existingIds: Array<number | Airport | Destination | AirlineModel> = [];
    @Input() resultCap = 4;

    @Input() searchFn!: (term: string) => Observable<any[]>;
    @Input() mapFn?: (raw: any) => any;
    @Input() idKey: string = 'id';

    @Input() getImage?: (item: any) => string | undefined;
    @Input() setImage?: (item: any, url?: string) => void;
    @Input() getCityForPhoto?: (item: any) => string;

    @Input() itemLabelSingular = 'item';
    @Input() customHeader?: TemplateRef<any>;
    @Input() showHeaderOnEmpty = false;
    @Input() showUpgradeModalOnLimit = false;
    @Input() showDealFrequency = false;

    @Output() added = new EventEmitter<any>();
    @Output() limitReached = new EventEmitter<void>();

    searchForm: FormGroup;
    dropdown: any[] = [];
    dropdownVisible = false;
    isSearchEmpty = true;

    constructor(private fb: FormBuilder, private toast: ToastrService) {
        this.searchForm = this.fb.group({ search: [''] });
    }

    ngOnInit(): void {
        this.setup();
    }

    focusInput() {
        if (this.inputEl) {
            this.inputEl.nativeElement.focus();
        }
    }
    private toId = (x: number | Object): number => {
        if (typeof x === 'number') {
            return x;
        }
        return x[this.idKey];
    };

    trackById = (_: number, item: any) => {
        return this.toId(item);
    };

    private buildExistingIdSet(): Set<number> {
        return new Set(
            (this.existingIds || [])
                .map(v => this.toId(v))
                .filter((n): n is number => Number.isFinite(n))
        );
    }

    private isAlreadySelected = (item: any): boolean => {
        const id = this.toId(item);
        return Number.isFinite(id) && this.buildExistingIdSet().has(id);
    };

    private setup(): void {
        this.searchForm
            .get('search')
            .valueChanges.pipe(
            debounceTime(300),
            map((term: string) => term.trim()),
            distinctUntilChanged(),
            tap((term: string) => {
                this.isSearchEmpty = term.length === 0;
                if (this.isSearchEmpty && this.showHeaderOnEmpty && this.customHeader) {
                    this.dropdownVisible = true;
                }
            }),
            filter((term: string) => {
                const ok = term.length > 0;
                if (!ok && this.showHeaderOnEmpty && this.customHeader) {
                    this.dropdown = [];
                    return false;
                }
                this.dropdownVisible = ok;
                return ok;
            }),
            switchMap(term =>
                this.searchFn(term).pipe(
                    catchError(err => {
                        console.error('search error', err);
                        return of([]);
                    })
                )
            ),
            map(results => (this.mapFn ? results.map(this.mapFn) : results)),
            map(items => {
                const idSet = this.buildExistingIdSet();
                return items.filter((it: any) => !idSet.has(this.toId(it)));
            })
        )
            .subscribe(items => {
                const slicedItems = items.slice(0, this.resultCap);
                this.dropdown = this.showDealFrequency
                    ? slicedItems.map(item => ({
                        ...item,
                        dealFrequencyClass: this.getDealFrequencyClass(item.dealFrequency)
                    }))
                    : slicedItems;
                this.dropdownVisible = this.dropdown.length > 0;
            });
    }

    onFocus(): void {
        if (this.isSearchEmpty && this.showHeaderOnEmpty && this.customHeader) {
            this.dropdownVisible = true;
        }
    }

    select(item: AirlineModel | Destination | Airport, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }

        if (this.isAlreadySelected(item)) {
            this.toast.info(`That ${this.itemLabelSingular} is already in your list`);
            this.dropdown = this.dropdown.filter(it => this.toId(it) !== this.toId(item));
            this.dropdownVisible = this.dropdown.length > 0;
            return;
        }

        const limitCheck = checkDepartureAirportLimit({
            currentCount: this.currentCount,
            maxCount: this.maxCount,
            showUpgradeModal: this.showUpgradeModalOnLimit,
            itemLabelSingular: this.itemLabelSingular
        }, this.toast);

        if (limitCheck.limitReached) {
            // Only emit event for upgrade modal; don't emit for ShowToast action or ReplaceOldest action
            if (limitCheck.action === LimitReachedAction.EmitEvent) {
                this.limitReached.emit();
            }
            this.dropdownVisible = false;
            return;
        }

        const finalize = (imgUrl?: string) => {
            if (this.setImage && imgUrl !== undefined) {
                this.setImage(item, imgUrl);
            }
            this.added.emit(item);
            this.searchForm.get('search')?.setValue('');
            this.dropdown = [];
            this.dropdownVisible = false;
        };

        const existingImg = this.getImage?.(item);
        if (existingImg) {
            finalize(existingImg);
            return;
        }

        finalize(undefined);
    }

    getDealFrequencyClass(frequency: DealFrequency | null): string {
        if (!frequency) {
            return '';
        }
        return DEAL_FREQUENCY_CLASS_MAP[frequency] || '';
    }
}
